import React, { useEffect, useState } from 'react';

import { LeaderboardData } from '../App';
import { censorProfanity } from '../utils/ProfanityFilter';

interface LeaderboardProps {
  leaderboard: LeaderboardData;
  currentPlayerId?: string;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({
  leaderboard,
  currentPlayerId,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Add debug logging
  useEffect(() => {
    console.log('Leaderboard component rendering with data:', leaderboard);
  }, [leaderboard]);

  // Get top 10 entries sorted by score
  const topEntries = [...leaderboard.entries]
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '8px',
    padding: isCollapsed ? '10px' : '15px',
    color: 'white',
    fontFamily: 'Arial, sans-serif',
    zIndex: 1000,
    width: isCollapsed ? 'auto' : '250px',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(5px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: isCollapsed ? '0' : '10px',
    fontSize: '18px',
    fontWeight: 'bold',
  };

  const toggleButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    fontSize: '20px',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    display: isCollapsed ? 'none' : 'table',
  };

  const rowStyle: React.CSSProperties = {
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '10px',
  };

  const cellStyle: React.CSSProperties = {
    padding: '6px 0',
    textAlign: 'left',
  };

  const rankCellStyle: React.CSSProperties = {
    ...cellStyle,
    width: '30px',
    fontWeight: 'bold',
    color: '#FFD700', // Gold color for rank
  };

  const nameCellStyle: React.CSSProperties = {
    ...cellStyle,
    width: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const scoreCellStyle: React.CSSProperties = {
    ...cellStyle,
    width: '70px',
    textAlign: 'right',
    fontWeight: 'bold',
  };

  const currentPlayerRowStyle: React.CSSProperties = {
    ...rowStyle,
    backgroundColor: 'rgba(255, 102, 0, 0.3)', // Highlight for current player
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        {isCollapsed ? 'Top Players' : 'Leaderboard'}
        <button
          style={toggleButtonStyle}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? '↓' : '↑'}
        </button>
      </div>
      <table style={tableStyle}>
        <tbody>
          {topEntries.map((entry, index) => (
            <tr
              key={entry.playerId}
              style={
                entry.playerId === currentPlayerId
                  ? currentPlayerRowStyle
                  : rowStyle
              }
            >
              <td style={rankCellStyle}>{index + 1}</td>
              <td style={nameCellStyle}>
                {censorProfanity(entry.playerName) ||
                  `Player ${entry.playerId.slice(0, 4)}`}
              </td>
              <td style={scoreCellStyle}>{entry.score}</td>
            </tr>
          ))}
          {topEntries.length === 0 && (
            <tr style={rowStyle}>
              <td colSpan={3} style={{ ...cellStyle, textAlign: 'center' }}>
                No scores yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
